import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { BankReviewSection } from '../components/facturen/BankReviewSection';
import { IncassoOverdrachtDialog } from '../components/facturen/IncassoOverdrachtDialog';
import { NewInvoiceDialog } from '../components/facturen/NewInvoiceDialog';
import {
  fetchInvoiceList,
  fetchVervallenList,
  omschrijfUitsluitReden,
  postIncassoActie,
  searchInvoices,
  type IncassoActie,
  type InvoiceListItem,
  type InvoiceListStatus,
  type VervallenListItem,
} from '../lib/invoices';
import {
  computeInvoiceStatus,
  INCASSO_FASE_COLORS,
  INCASSO_FASE_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  type InvoiceStatus,
} from '../lib/invoice-status';
import { useInvoiceDetailDrawerStore } from '../stores/invoice-detail-drawer-store';
import { BRAND_COLORS } from '../lib/order-constants';
import { formatCurrency, formatDate } from '../lib/format';

type PageFilter = InvoiceListStatus | 'vervallen' | 'incasso';

const FILTERS: Array<{ value: PageFilter; label: string }> = [
  { value: 'open', label: 'Openstaand' },
  { value: 'partial', label: 'Deelbetaling' },
  { value: 'paid', label: 'Voldaan' },
  { value: 'all', label: 'Alle' },
  { value: 'vervallen', label: 'Vervallen (alles)' },
  { value: 'incasso', label: 'Incasso' },
];

function isVervallenItem(i: InvoiceListItem | VervallenListItem): i is VervallenListItem {
  return 'fase' in i;
}

function isOverdue(dueDate: string, status: InvoiceStatus): boolean {
  if (status === 'paid') return false;
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

export function FacturenPage() {
  const openDrawer = useInvoiceDetailDrawerStore((s) => s.open);
  const [filter, setFilter] = useState<PageFilter>('open');
  const [items, setItems] = useState<Array<InvoiceListItem | VervallenListItem>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<InvoiceListItem[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [actieBusy, setActieBusy] = useState<string | null>(null);
  const [overdracht, setOverdracht] = useState<{ id: string; name: string } | null>(null);

  async function doeIncassoActie(row: VervallenListItem, actie: IncassoActie) {
    let reden: string | undefined;
    if (actie === 'uitsluiten') {
      reden = window.prompt(`Reden om ${row.name} uit te sluiten van incasso:`)?.trim();
      if (!reden) return;
    }
    if (actie === 'al-bij-bos' && !window.confirm(`${row.name} markeren als "ligt al bij Bos Incasso"? Er wordt niets verstuurd.`)) {
      return;
    }
    setActieBusy(row.id);
    try {
      await postIncassoActie(row.id, actie, reden);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Incasso-actie mislukt');
    } finally {
      setActieBusy(null);
    }
  }

  // List fetch when filter changes (of na een bank-koppeling)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const load =
      filter === 'vervallen' || filter === 'incasso'
        ? fetchVervallenList(filter)
        : fetchInvoiceList(filter);
    load
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Kon facturen niet laden');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, refreshKey]);

  // Debounced server search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      searchInvoices(trimmed, controller.signal)
        .then((results) => {
          setSearchResults(results);
          setSearching(false);
        })
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setSearching(false);
          setSearchResults([]);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const isSearching = query.trim().length >= 2;
  const displayItems = isSearching ? searchResults ?? [] : items;

  const totals = useMemo(() => {
    let openSum = 0;
    let totalSum = 0;
    for (const i of displayItems) {
      openSum += i.openAmount;
      totalSum += i.totalInclBtw;
    }
    return { openSum, totalSum };
  }, [displayItems]);

  return (
    <>
      <IncassoOverdrachtDialog
        invoiceId={overdracht?.id ?? null}
        invoiceName={overdracht?.name ?? ''}
        onClose={() => setOverdracht(null)}
        onTransferred={() => setRefreshKey((k) => k + 1)}
      />
      <NewInvoiceDialog
        isOpen={newInvoiceOpen}
        onClose={() => setNewInvoiceOpen(false)}
        onCreated={(invoiceId) => {
          setNewInvoiceOpen(false);
          setRefreshKey((k) => k + 1);
          openDrawer(invoiceId);
        }}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Facturen</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {isSearching
                  ? `Zoekresultaten voor "${query}"`
                  : filter === 'vervallen'
                    ? 'Alle vervallen facturen sinds 2024'
                    : filter === 'incasso'
                      ? 'Incasso-dossiers (kandidaten en overdrachten)'
                      : 'Facturen van de laatste 90 dagen'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-1 justify-end">
              <div className="relative flex-1 max-w-sm">
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Zoek factuurnr, klant, postcode, WO..."
                  className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setNewInvoiceOpen(true)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nieuwe factuur
              </button>
            </div>
          </div>

          {/* Filter chips — verborgen tijdens zoeken (zoek doorzoekt alle facturen) */}
          {!isSearching && (
            <div className="flex items-center gap-1 mt-3">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={clsx(
                    'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                    filter === f.value
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-slate-500 hover:bg-slate-50'
                  )}
                >
                  {f.label}
                </button>
              ))}
              {!loading && items.length > 0 && (
                <span className="ml-auto text-xs text-slate-500">
                  {filter === 'incasso'
                    ? `${items.filter((i) => isVervallenItem(i) && i.incassoStatus === 'kandidaat').length} kandidaat · ${items.filter((i) => isVervallenItem(i) && i.incassoStatus === 'overgedragen').length} bij incasso · `
                    : ''}
                  {items.length} factu{items.length === 1 ? 'ur' : 'ren'} ·{' '}
                  <span className="text-slate-700 font-medium">
                    {formatCurrency(totals.openSum)} openstaand
                  </span>{' '}
                  van {formatCurrency(totals.totalSum)}
                </span>
              )}
            </div>
          )}
        </div>

        <BankReviewSection onMutated={() => setRefreshKey((k) => k + 1)} />

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          ) : !isSearching && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-sm">
                {filter === 'vervallen'
                  ? 'Geen vervallen facturen'
                  : filter === 'incasso'
                    ? 'Geen incasso-dossiers'
                    : `Geen ${FILTERS.find((f) => f.value === filter)?.label.toLowerCase() || ''} facturen in de laatste 90 dagen`}
              </p>
            </div>
          ) : isSearching && !searching && displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">Geen facturen gevonden voor "{query}"</p>
              <button
                onClick={() => setQuery('')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Zoekopdracht wissen
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-6 py-2">Factuur</th>
                  <th className="text-left font-medium px-4 py-2">Klant</th>
                  <th className="text-left font-medium px-4 py-2">Merk</th>
                  <th className="text-left font-medium px-4 py-2">Plaats</th>
                  <th className="text-left font-medium px-4 py-2">Datum</th>
                  <th className="text-left font-medium px-4 py-2">Vervalt</th>
                  <th className="text-right font-medium px-4 py-2">Totaal</th>
                  <th className="text-right font-medium px-4 py-2">Open</th>
                  {(filter === 'vervallen' || filter === 'incasso') && !isSearching ? (
                    <>
                      <th className="text-right font-medium px-4 py-2">Dagen open</th>
                      <th className="text-left font-medium px-4 py-2">Fase</th>
                      {filter === 'incasso' && (
                        <th className="text-left font-medium px-4 py-2">Acties</th>
                      )}
                    </>
                  ) : (
                    <th className="text-left font-medium px-4 py-2">Status</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayItems.map((row) => {
                  const status = computeInvoiceStatus({
                    totalInclBtw: row.totalInclBtw,
                    openAmount: row.openAmount,
                    transactionTotal: row.transactionTotal,
                    paid: row.paid,
                  });
                  const overdue = isOverdue(row.dueDate, status);
                  return (
                    <tr
                      key={row.id}
                      onClick={() => openDrawer(row.id)}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-3 font-medium text-slate-900">
                        {row.name || '—'}
                        {row.isCreditfactuur && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700">
                            Credit
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.customerName}</td>
                      <td className="px-4 py-3">
                        {row.brand ? (
                          <span
                            className={clsx(
                              'text-[10px] px-1.5 py-0.5 rounded font-medium',
                              BRAND_COLORS[row.brand] || 'bg-slate-100 text-slate-600'
                            )}
                          >
                            {row.brand === 'SCHOONHUISSHOP' ? 'SHS' : row.brand}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {[row.postcode, row.city].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {formatDate(row.invoiceDate) || '—'}
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-3',
                          overdue ? 'text-red-600 font-medium' : 'text-slate-500'
                        )}
                      >
                        {formatDate(row.dueDate) || '—'}
                        {overdue && <span className="ml-1 text-[10px] uppercase">over</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-900">
                        {formatCurrency(row.totalInclBtw)}
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-3 text-right',
                          row.openAmount > 0.01 ? 'text-amber-700 font-medium' : 'text-slate-400'
                        )}
                      >
                        {row.openAmount > 0.01 ? formatCurrency(row.openAmount) : '—'}
                      </td>
                      {(filter === 'vervallen' || filter === 'incasso') && !isSearching && isVervallenItem(row) ? (
                        <>
                          <td
                            className={clsx(
                              'px-4 py-3 text-right',
                              row.daysOpen >= 37 ? 'text-red-600 font-medium' : 'text-slate-600'
                            )}
                          >
                            {row.daysOpen}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={clsx(
                                'text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap',
                                INCASSO_FASE_COLORS[row.fase] || 'bg-slate-100 text-slate-600'
                              )}
                              title={
                                row.uitsluitReden
                                  ? `Uitgesloten: ${omschrijfUitsluitReden(row.uitsluitReden)}`
                                  : row.overgedragenAt
                                    ? `Overgedragen op ${formatDate(row.overgedragenAt)}`
                                    : row.toegezegdBetaaldatum
                                      ? `Betaaltoezegging: ${formatDate(row.toegezegdBetaaldatum)}`
                                      : undefined
                              }
                            >
                              {INCASSO_FASE_LABELS[row.fase] || row.fase}
                            </span>
                          </td>
                          {filter === 'incasso' && (
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1.5">
                                {row.incassoStatus === 'kandidaat' && (
                                  <>
                                    <button
                                      type="button"
                                      disabled={actieBusy === row.id}
                                      onClick={() => setOverdracht({ id: row.id, name: row.name })}
                                      className="px-2 py-1 text-[11px] font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                    >
                                      Overdragen
                                    </button>
                                    <button
                                      type="button"
                                      disabled={actieBusy === row.id}
                                      onClick={() => doeIncassoActie(row, 'al-bij-bos')}
                                      className="px-2 py-1 text-[11px] font-medium rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                      Al bij Bos
                                    </button>
                                    <button
                                      type="button"
                                      disabled={actieBusy === row.id}
                                      onClick={() => doeIncassoActie(row, 'uitsluiten')}
                                      className="px-2 py-1 text-[11px] font-medium rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                      Uitsluiten
                                    </button>
                                  </>
                                )}
                                {row.incassoStatus === 'uitgesloten' && (
                                  <button
                                    type="button"
                                    disabled={actieBusy === row.id}
                                    onClick={() => doeIncassoActie(row, 'terug-kandidaat')}
                                    className="px-2 py-1 text-[11px] font-medium rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                  >
                                    Weer kandidaat
                                  </button>
                                )}
                                {row.incassoStatus === 'overgedragen' && (
                                  <button
                                    type="button"
                                    disabled={actieBusy === row.id}
                                    onClick={() => doeIncassoActie(row, 'oninbaar')}
                                    className="px-2 py-1 text-[11px] font-medium rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                  >
                                    Oninbaar
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </>
                      ) : (
                        <td className="px-4 py-3">
                          <span
                            className={clsx(
                              'text-[10px] px-1.5 py-0.5 rounded font-medium',
                              INVOICE_STATUS_COLORS[status]
                            )}
                          >
                            {INVOICE_STATUS_LABELS[status]}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
